<template>
  <div class="admin-order-container">
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-row :gutter="20" align="middle">
        <el-col :span="18">
          <el-space>
            <!-- 时间范围按钮 -->
            <el-button
                @click="handleTimeRange('今日')"
                :style="searchForm.timeRange === '今日' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#adce7d', borderColor: '#adce7d' }"
                class="time-range-btn"
            >今日</el-button>

            <el-button
                @click="handleTimeRange('昨日')"
                :style="searchForm.timeRange === '昨日' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#82baee', borderColor: '#82baee' }"
                class="time-range-btn"
            >昨日</el-button>

            <el-button
                @click="handleTimeRange('本周')"
                :style="searchForm.timeRange === '本周' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#d47970', borderColor: '#d47970' }"
                class="time-range-btn"
            >本周</el-button>

            <el-button
                @click="handleTimeRange('本月')"
                :style="searchForm.timeRange === '本月' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#caa807', borderColor: '#caa807', backgroundColor: '#fcca00' }"
                class="time-range-btn month-btn"
            >本月</el-button>

            <!-- 日期范围选择 -->
            <el-date-picker
                v-model="searchForm.startDate"
                type="date"
                placeholder="开始日期"
                style="width: 150px"
                @change="handleDateRangeChange"
            />
            <span>至</span>
            <el-date-picker
                v-model="searchForm.endDate"
                type="date"
                placeholder="结束日期"
                style="width: 150px"
                @change="handleDateRangeChange"
            />

            <!-- 搜索框 -->
            <el-input
                v-model="searchForm.keyword"
                placeholder="搜索订单编号、插件名称、买家"
                style="width: 300px"
                clearable
                :prefix-icon="Search"
                @clear="clearSearch"
                @keyup.enter="handleSearch"
            />
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </el-space>
        </el-col>

        <el-col :span="6" style="text-align: right">
          <div class="income-display">
            <span class="income-label">本月收益：</span>
            <span class="income-value">¥{{ monthlyIncome.toFixed(2) }}</span>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 表格区域 -->
    <div class="table-area card">
      <div class="table-header">
        <h3 class="table-title">
          <i class="el-icon-shopping-bag-1"></i> 订单管理
          <span class="total-count">(共 {{ total }} 条)</span>
        </h3>
        <div class="table-actions">
          <el-button size="small" @click="refreshData">
            <i class="el-icon-refresh"></i> 刷新
          </el-button>
        </div>
      </div>

      <el-table
          :data="tableData"
          v-loading="loading"
          :cell-style="tableCellStyle"
          :header-cell-style="tableHeaderStyle"
          style="width: 100%"
          border
          stripe
          height="calc(100vh - 300px)"
      >
        <el-table-column prop="orderNo" label="订单编号" min-width="280" align="center"/>
        <el-table-column prop="productCode" label="插件编码" width="160" align="center"/>
        <el-table-column prop="productName" label="插件名称" width="160" align="center"/>
        <el-table-column prop="buyerUsername" label="购买者" width="160" align="center"/>
        <el-table-column prop="totalFee" label="金额（元）" width="120" align="center">
          <template #default="scope">
            <span style="color: #dc2626; font-weight: 500;">
                ¥{{ scope.row.totalFee?.toFixed(2) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="purchasedDays" label="时长(天)" width="120" align="center"/>

        <el-table-column prop="paymentMethodName" label="支付方式" width="120" align="center"/>

        <el-table-column prop="paidAt" label="购买时间" width="180" align="center">
          <template #default="scope">
            {{ scope.row.paidAt ? formatDateTime(scope.row.paidAt) : '-' }}
          </template>
        </el-table-column>

        <el-table-column prop="paymentStatus" label="状态" width="90" align="center" fixed="right">
          <template #default="scope">
            <el-tag :type="getStatusTagType(scope.row.paymentStatus)">
              {{ formatStatus(scope.row.paymentStatus) }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
            v-model:current-page="currentPage"
            :page-size="pageSize"
            :total="total"
            :pager-count="5"
            layout="prev, pager, next"
            @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import request from '@/utils/request'
import dayjs from "dayjs";

// 响应式数据
const loading = ref(false)
const tableData = ref([])
const monthlyIncome = ref(0)

// 当前激活的时间范围按钮
const activeTimeRange = ref('')

// 分页相关数据
const currentPage = ref(1)
const pageSize = ref(15)
const total = ref(0)

// 搜索条件
const searchForm = reactive({
  timeRange: '',
  startDate: '',
  endDate: '',
  keyword: ''
})

// 获取订单列表
const getOrderList = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      size: pageSize.value
    }

    const response = await request.get('/order/all', { params })
    tableData.value = response.data.list
    total.value = response.data.total
  } catch (error) {
    if (error.response && error.response.status === 403) {
      ElMessage.error('权限不足，无法查看订单')
    } else {
      console.error('获取订单列表失败:', error)
    }
  } finally {
    loading.value = false
  }
}

// 清空搜索
const clearSearch = () => {
  searchForm.keyword = ''
  handleSearch()
}

// 搜索订单
const handleSearch = async () => {
  if (!searchForm.keyword && !searchForm.timeRange && !searchForm.startDate) {
    resetSearch()
    return
  }

  loading.value = true
  try {
    const params = {
      keyword: searchForm.keyword,
      page: currentPage.value,
      size: pageSize.value
    }

    if (searchForm.timeRange || (searchForm.startDate && searchForm.endDate)) {
      if (searchForm.timeRange) {
        params.timeRange = searchForm.timeRange
      }
      if (searchForm.startDate && searchForm.endDate) {
        params.startDate = formatDateTime(searchForm.startDate)
        params.endDate = formatDateTime(searchForm.endDate)
      }

      const response = await request.get('/order/search/byTimeRange', { params })
      tableData.value = response.data.list
      total.value = response.data.total
    } else {
      const response = await request.get('/order/search', { params })
      tableData.value = response.data.list
      total.value = response.data.total
    }
  } catch (error) {
    if (error.response && error.response.status === 403) {
      ElMessage.error('权限不足，无法搜索订单')
    } else if (error.response && error.response.status === 400) {
      console.log("搜索参数错误")
    } else {
      console.log("搜索订单失败")
    }
    console.error(error)
  } finally {
    loading.value = false
  }
}

// 时间范围快速选择
const handleTimeRange = (range) => {
  if (activeTimeRange.value === range) {
    activeTimeRange.value = ''
    searchForm.timeRange = ''
    searchForm.startDate = ''
    searchForm.endDate = ''

    currentPage.value = 1
    getOrderList()
  } else {
    activeTimeRange.value = range
    searchForm.timeRange = range
    searchForm.startDate = ''
    searchForm.endDate = ''

    currentPage.value = 1
    handleSearch()
  }
}

// 自定义日期范围
const handleDateRangeChange = () => {
  searchForm.timeRange = ''
  activeTimeRange.value = ''

  if (searchForm.startDate && searchForm.endDate) {
    currentPage.value = 1
    handleSearch()
  }
}

// 重置搜索
const resetSearch = () => {
  searchForm.keyword = ''
  searchForm.timeRange = ''
  searchForm.startDate = ''
  searchForm.endDate = ''
  activeTimeRange.value = ''
  currentPage.value = 1
  getOrderList()
}

// 获取本月收益
const getMonthlyIncome = async () => {
  try {
    const response = await request.get('/order/monthly-income')
    monthlyIncome.value = response.data
  } catch (error) {
    if (error.response && error.response.status === 403) {
      ElMessage.error('权限不足，无法查看收益')
    } else {
      console.log("获取收益失败")
    }
    console.error(error)
  }
}

// 当前页改变
const handleCurrentChange = (newPage) => {
  currentPage.value = newPage
  if (searchForm.keyword || searchForm.timeRange || searchForm.startDate) {
    handleSearch()
  } else {
    getOrderList()
  }
}

// 支付状态格式化
const formatStatus = (status) => {
  const statusMap = {
    0: '未支付',
    1: '完成',
    2: '已退款',
    3: '已关闭'
  }
  return statusMap[status] || '未知'
}

// 状态标签类型
const getStatusTagType = (status) => {
  const typeMap = {
    0: 'warning',
    1: 'success',
    2: 'info',
    3: 'danger'
  }
  return typeMap[status] || 'info'
}

// 格式化日期时间
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-'
  return dayjs(dateTimeStr).format('YYYY-MM-DD hh:mm:ss')
}

// 刷新方法
const refreshData = () => {
  currentPage.value = 1
  getOrderList()
}

// 表格样式方法
const tableCellStyle = ({ row, column, rowIndex, columnIndex }) => {
  // 可以针对特定列设置样式
  if (column.property === 'totalFee') {
    return {
      color: '#dc2626',
      fontWeight: '500'
    }
  }
  if (column.property === 'paidAt') {
    return {
      color: '#1890ff',
      fontWeight: '500'
    }
  }
  return {}
}

const tableHeaderStyle = ({ row, column, rowIndex, columnIndex }) => {
  return {
    background: '#f8f9fa',
    fontWeight: '600',
    color: '#495057',
    borderBottom: '2px solid #dee2e6'
  }
}

onMounted(async () => {
  getOrderList()
  getMonthlyIncome()
})
</script>

<style scoped>
.admin-order-container {
  padding: 20px;
  margin: 0 auto;
}

.card {
  background: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* 搜索区域样式 */
.search-area {
  margin-bottom: 10px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
  border-radius: 5px;
}

.time-range-btn {
  width: 60px;
  height: 25px;
}

.income-display {
  font-size: 16px;
}

.income-label {
  color: #606266;
}

.income-value {
  color: #e6a23c;
  font-weight: bold;
  font-size: 18px;
}

/* 表格区域样式 */
.table-area {
  padding: 10px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.table-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-title i {
  color: #409eff;
}

.total-count {
  font-size: 14px;
  font-weight: normal;
  color: #64748b;
  margin-left: 8px;
}

.table-actions {
  display: flex;
  gap: 8px;
}

/* 分页样式 */
.pagination-container {
  padding-top: 10px;
  display: flex;
  justify-content: center;
}

/* 动画效果 */
:deep(.el-table__row:hover) {
  background-color: #f7f9fc;
}

:deep(.el-table__body tr.hover-row>td) {
  background-color: #f7f9fc !important;
}

/* 滚动条样式 */
:deep(.el-table__body-wrapper::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

:deep(.el-table__body-wrapper::-webkit-scrollbar-track) {
  background: #f1f1f1;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper::-webkit-scrollbar-thumb:hover) {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .admin-order-container {
    padding: 15px;
  }

  .search-area {
    padding: 12px;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .table-actions {
    align-self: flex-end;
  }
}
</style>